import { EventSourceMessage } from "@microsoft/fetch-event-source";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { useError } from "../contexts/ErrorContext";
import { errorToString, isError } from "../types/typeGuards";
import {
  Carousel,
  ChatItem,
  Message,
  MessageBuffer,
  Panel,
  parseMessage,
  parseThread,
  Source,
  Thread,
} from "../types/types";
import { keysToCamelCase } from "../utils/caseConversion";
import { LEGAL_TASK_PROGRESS_EVENT } from "../utils/legalSearchTaskFlow";
import log from "../utils/logger";
import useSseWithMsal from "./useSseWithMsal";
import { useThread } from "./useThread";
import { endpointsCf } from "../config/componentsConfig";

const THREAD_DRAFT_MESSAGES_KEY_PREFIX = "infravia-thread-draft-messages:";

type StoredMessage = Omit<Message, "createdAt"> & { createdAt: number };

function loadDraftMessages(threadId: string): Message[] {
  try {
    const raw = sessionStorage.getItem(
      `${THREAD_DRAFT_MESSAGES_KEY_PREFIX}${threadId}`
    );
    if (!raw) return [];
    const parsed = JSON.parse(raw) as StoredMessage[];
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((m) => m && typeof m.id === "string" && m.threadId === threadId)
      .map((m) => ({ ...m, createdAt: new Date(m.createdAt) }));
  } catch {
    return [];
  }
}

function saveDraftMessages(threadId: string, messages: Message[]): void {
  try {
    const stored: StoredMessage[] = messages
      .filter((m) => m.threadId === threadId)
      // Les messages optimistes sont transitoires UI et ne doivent jamais être persistés.
      // Sinon, en réouverture de thread, ils peuvent se superposer aux messages backend.
      .filter((m) => !String(m.id).startsWith("optimistic-"))
      // Ne pas gonfler la session : on garde un historique raisonnable.
      .slice(-250)
      .map((m) => ({
        ...m,
        createdAt: m.createdAt.getTime(),
      }));
    sessionStorage.setItem(
      `${THREAD_DRAFT_MESSAGES_KEY_PREFIX}${threadId}`,
      JSON.stringify(stored)
    );
  } catch {
    /* ignore */
  }
}

function clearDraftMessages(threadId: string): void {
  try {
    sessionStorage.removeItem(`${THREAD_DRAFT_MESSAGES_KEY_PREFIX}${threadId}`);
  } catch {
    /* ignore */
  }
}

/** Ordre stable pour ignorer les permutations du tableau (SSE + fetch historique). */
function compareMessagesStable(a: Message, b: Message): number {
  const t = a.createdAt.getTime() - b.createdAt.getTime();
  if (t !== 0) return t;
  return String(a.id).localeCompare(String(b.id));
}

function sortMessagesStable(messages: Message[]): Message[] {
  return [...messages].sort(compareMessagesStable);
}

function pickRicherMessage(a: Message, b: Message): Message {
  if (typeof a.content === "string" && typeof b.content === "string") {
    if (a.content.length > b.content.length) return a;
    if (b.content.length > a.content.length) return b;
  }
  return a.createdAt.getTime() >= b.createdAt.getTime() ? a : b;
}

/** Statut redondant émis par le back ; l’UI utilise déjà la barre de tâche / le placeholder. */
const REDUNDANT_EXHAUSTIVE_SEARCH_STATUS_RE =
  /georges\s+is\s+now\s+performing\s+exhaustive\s+document\s+search/i;

function isRedundantExhaustiveSearchStatusThought(m: Message): boolean {
  if (m.type !== "thought") return false;
  if (typeof m.content !== "string") return false;
  const flat = m.content
    .replace(/<[^>]*>/g, " ")
    .replace(/[*_`#]/g, "")
    .replace(/\s+/g, " ")
    .trim();
  return REDUNDANT_EXHAUSTIVE_SEARCH_STATUS_RE.test(flat);
}

function filterMessagesForDisplay(messages: Message[]): Message[] {
  return messages.filter((m) => !isRedundantExhaustiveSearchStatusThought(m));
}

function dedupeUserMessages(messages: Message[]): Message[] {
  const sorted = sortMessagesStable(messages);

  // 1) Retire les bulles user optimistes si le backend a déjà renvoyé la même question.
  const hasRealUserByKey = new Set<string>();
  for (const m of sorted) {
    if (m.type !== "user") continue;
    if (String(m.id).startsWith("optimistic-")) continue;
    if (typeof m.content !== "string") continue;
    hasRealUserByKey.add(`${m.threadId}::${m.content.trim()}`);
  }

  const withoutOptimisticEcho = sorted.filter((m) => {
    if (m.type !== "user") return true;
    if (!String(m.id).startsWith("optimistic-")) return true;
    if (typeof m.content !== "string") return true;
    return !hasRealUserByKey.has(`${m.threadId}::${m.content.trim()}`);
  });

  // 2) Garde-fou: évite les doublons backend consécutifs (même texte, même thread, quasi même timestamp).
  const result: Message[] = [];
  for (const m of withoutOptimisticEcho) {
    const prev = result[result.length - 1];
    if (
      prev &&
      prev.type === "user" &&
      m.type === "user" &&
      typeof prev.content === "string" &&
      typeof m.content === "string" &&
      prev.threadId === m.threadId &&
      prev.content.trim() === m.content.trim() &&
      Math.abs(prev.createdAt.getTime() - m.createdAt.getTime()) <= 5000
    ) {
      continue;
    }
    result.push(m);
  }

  return result;
}

function panelFirstMessageTime(panel: Panel): number {
  if (!panel.messages.length) return 0;
  return Math.min(...panel.messages.map((m) => m.createdAt.getTime()));
}

interface ChatHandlers {
  setQuery: (query: string) => void;
  setIsReformulation: (value: boolean) => void;
}

export const useChat = (
  threadsEndpoint: string,
  messagesEndpoint: string,
  chatEndpoint: string,
  handlers: ChatHandlers,
  exhaustiveSearchResumeEndpoint?: string
) => {
  // log.info("useChat hook initialized"); // Logging hook initialization

  const [messages, setMessages] = useState<Message[]>([]);
  const [selectedSource, setSelectedSource] = useState<Source | null>(null);
  const buffers = useRef<{ [key: string]: MessageBuffer }>({});
  const [chatItems, setChatItems] = useState<ChatItem[]>([]);
  const selectedThreadIdRef = useRef<string | null>(null);
  
  // Effet pour réinitialiser l'état au chargement de la page
  useEffect(() => {
    setMessages([]);
    setChatItems([]);
    buffers.current = {};
    setSelectedSource(null);
  }, []); // Se déclenche uniquement au montage du composant

  const {
    threads,
    changeThread,
    selectedThread,
    messages: threadMessages,
    fetchMessages,
    setThreads,
    deleteThread,
    renameThread,
  } = useThread(threadsEndpoint, messagesEndpoint);
  useEffect(() => {
    selectedThreadIdRef.current = selectedThread?.id ?? null;
  }, [selectedThread?.id]);


  const { addError } = useError();

  // Handle source change
  const changeSelectedSource = useCallback(
    (source: Source) => {
      console.log("Source changed", source);
      setSelectedSource(source);
    },
    [setSelectedSource]
  );

  const processMessage = useCallback((data: any[], message: Message) => {
    // Append or create a new buffer for the message
    if (buffers.current[message.id]) {
      // log.debug(`Appending to buffer for message ID: ${message.id}`); // Log buffer append
      buffers.current[message.id].append(message);
    } else {
      // log.debug(`Creating new buffer for message ID: ${message.id}`); // Log new buffer creation
      buffers.current[message.id] = new MessageBuffer(message);
    }

    const updatedMessages = [
      ...data.filter((m) => m.id !== message.id),
      buffers.current[message.id].message,
    ];
    // log.debug("Updated messages", updatedMessages); 
    return updatedMessages;
  }, []);

  const processThread = useCallback(
    (data: any[], thread: Thread) => {
      const normalizedThread = { ...thread, id: String(thread.id) } as Thread;
      setThreads((prevThreads) => {
        const incomingId = String(normalizedThread.id);
        const existingIndex = prevThreads.findIndex(
          (t) => String(t.id) === incomingId
        );
        if (existingIndex === -1) {
          return [normalizedThread, ...prevThreads];
        }
        const existing = prevThreads[existingIndex];
        const updated = [...prevThreads];
        updated.splice(existingIndex, 1);
        return [{ ...existing, ...normalizedThread }, ...updated];
      });
      // Ne pas forcer un changement de thread si l'utilisateur consulte déjà
      // une autre conversation : cela mélange l'UI pendant un stream en cours.
      if (
        !selectedThread ||
        String(selectedThread.id) === String(normalizedThread.id)
      ) {
        changeThread(normalizedThread, false);
      }
      return data;
    },
    [changeThread, selectedThread, setThreads]
  );

  // Handle incoming messages from the SSE
  const handleMessage = useCallback(
    (data: any[], event: EventSourceMessage) => {
      // log.debug("handleMessage called with event:", event);
      // log.debug("Current data is", data);
      try {
        const eventData = keysToCamelCase(JSON.parse(event.data));
        switch (eventData.objectType) {
          case "LegalSearchTaskProgress":
            window.dispatchEvent(
              new CustomEvent(LEGAL_TASK_PROGRESS_EVENT, {
                detail: keysToCamelCase(eventData.data),
              })
            );
            return data;
          case "Message":
            const message = parseMessage(eventData.data);
            // log.debug("Parsed message:", message);
            
            if (message.type === "reformulation") {
              handlers.setIsReformulation(true);
              return processMessage(data, message);
            }
            
            return processMessage(data, message);
          case "Thread":
            const thread = parseThread(eventData.data);
            // log.debug("Parsed thread:", thread);
            return processThread(data, thread);
          default:
            return data;
        }
      } catch (error) {
        const errorMessage = errorToString(error);
        log.error("Error processing SSE message:", errorMessage);
        addError(`Error processing message: ${errorMessage}`);
        return data;
      }
    },
    [processMessage, processThread, addError, handlers]
  );

  // Transform the flat list of messages into a list of chat items (messages and carousels)
  const transformList = useCallback((messages: Message[]): ChatItem[] => {
    // log.debug("Transforming messages into chat items"); // Log start of transformation

    const carousels: { [key: string]: Carousel } = {};
    const transformedList: ChatItem[] = [];

    const orderedMessages = sortMessagesStable(messages);

    orderedMessages.forEach((message) => {
      if (message.carousel) {
        const { carouselId, panelId } = message.carousel;

        if (!carousels[carouselId]) {
          carousels[carouselId] = {
            id: carouselId,
            type: "carousel",
            createdAt: message.createdAt,
            panels: [],
          };
        }

        let panel = carousels[carouselId].panels.find((p) => p.id === panelId);
        if (!panel) {
          // log.debug(
          //   `Creating new panel in carousel ${carouselId} for panel ${panelId}`
          // ); // Log panel creation
          panel = {
            id: panelId,
            title: message.carousel.panelLabel || "Untitled Panel",
            messages: [],
          };
          carousels[carouselId].panels.push(panel);
        }

        panel.messages.push(message);
      } else {
        transformedList.push(message);
      }
    });

    for (const carouselId in carousels) {
      const carousel = carousels[carouselId];
      carousel.panels.sort((a, b) => {
        const ta = panelFirstMessageTime(a);
        const tb = panelFirstMessageTime(b);
        if (ta !== tb) return ta - tb;
        return String(a.id).localeCompare(String(b.id));
      });
      for (const panel of carousel.panels) {
        panel.messages.sort(compareMessagesStable);
      }
      const times = carousel.panels.flatMap((p) =>
        p.messages.map((m) => m.createdAt.getTime())
      );
      if (times.length) {
        carousel.createdAt = new Date(Math.min(...times));
      }
      transformedList.push(carousel);
    }

    // log.debug("Transformed list created:", transformedList); // Log the transformed list
    return transformedList;
  }, []);

  // Hook for managing SSE connection and receiving messages
  const {
    execute: executeQuery,
    data: messagesQuery,
    abort: abortQuery,
    isLoading: isLoadingQuery,
    activeThreadIds,
  } = useSseWithMsal({ handleMessage });

  const isLoadingSelectedThread = useMemo(() => {
    if (!isLoadingQuery) return false;
    if (!selectedThread?.id) return false;
    return activeThreadIds.includes(selectedThread.id);
  }, [isLoadingQuery, selectedThread?.id, activeThreadIds]);

  const previousSelectedThreadIdRef = useRef<string | null>(null);
  useEffect(() => {
    const previous = previousSelectedThreadIdRef.current;
    const current = selectedThread?.id ?? null;
    // Politique uniforme: dès qu'on quitte la conversation courante, on coupe le stream.
    if (isLoadingQuery && previous && previous !== current) {
      abortQuery();
    }
    previousSelectedThreadIdRef.current = current;
  }, [selectedThread?.id, isLoadingQuery, abortQuery]);

  /** Bulle user affichée tout de suite (ex. modale ExhaustiveSearch) — retirée quand le SSE renvoie le vrai message user. */
  const appendOptimisticUserMessage = useCallback(
    (text: string, threadId: string) => {
      setMessages((prev) => {
        const maxT = prev.reduce(
          (acc, m) => Math.max(acc, m.createdAt.getTime()),
          0
        );
        const msg: Message = {
          id: `optimistic-${uuidv4()}`,
          createdAt: new Date(maxT + 1),
          type: "user",
          content: text,
          feedback: false,
          threadId,
          userId: "",
        };
        return [...prev, msg];
      });
    },
    []
  );

  const removeOptimisticUserMessages = useCallback(() => {
    setMessages((prev) =>
      prev.filter((m) => !String(m.id).startsWith("optimistic-"))
    );
  }, []);

  /** Placeholder "thought" (ex. Georges is thinking…) quand un job tourne mais que le SSE n'a pas encore renvoyé. */
  const appendOptimisticThoughtMessage = useCallback(
    (text: string, threadId: string) => {
      setMessages((prev) => {
        const maxT = prev.reduce(
          (acc, m) => Math.max(acc, m.createdAt.getTime()),
          0
        );
        const msg: Message = {
          id: `optimistic-thought-${uuidv4()}`,
          createdAt: new Date(maxT + 1),
          type: "thought",
          content: text,
          feedback: false,
          threadId,
          userId: "",
        };
        return [...prev, msg];
      });
    },
    []
  );

  const upsertOptimisticThoughtMessage = useCallback(
    (text: string, threadId: string) => {
      setMessages((prev) => {
        const existing = prev.find(
          (m) =>
            m.type === "thought" &&
            m.threadId === threadId &&
            String(m.id).startsWith("optimistic-thought-")
        );
        if (!existing) {
          const maxT = prev.reduce(
            (acc, m) => Math.max(acc, m.createdAt.getTime()),
            0
          );
          const msg: Message = {
            id: `optimistic-thought-${uuidv4()}`,
            createdAt: new Date(maxT + 1),
            type: "thought",
            content: text,
            feedback: false,
            threadId,
            userId: "",
          };
          return [...prev, msg];
        }
        return prev.map((m) =>
          m.id === existing.id ? { ...m, content: text } : m
        );
      });
    },
    []
  );

  const removeOptimisticThoughtMessages = useCallback(() => {
    setMessages((prev) =>
      prev.filter((m) => !String(m.id).startsWith("optimistic-thought-"))
    );
  }, []);

  /**
   * Arrêt utilisateur (croix) : coupe le SSE, supprime le contenu partiel après la dernière question,
   * affiche un message système (sans attendre le serveur).
   */
  const interruptStreamForThread = useCallback(
    (threadId: string) => {
      setMessages((prev) => {
        const noDupInterrupt = prev.filter(
          (m) =>
            !(
              m.threadId === threadId &&
              m.type === "system" &&
              String(m.id).startsWith("system-interrupt-")
            )
        );
        const sorted = sortMessagesStable(noDupInterrupt);
        let lastUserIdx = -1;
        for (let i = sorted.length - 1; i >= 0; i--) {
          if (sorted[i].threadId === threadId && sorted[i].type === "user") {
            lastUserIdx = i;
            break;
          }
        }
        if (lastUserIdx === -1) return prev;
        const head = sorted.slice(0, lastUserIdx + 1);
        const interruptMsg: Message = {
          id: `system-interrupt-${uuidv4()}`,
          type: "system",
          content:
            "Cette conversation a été interrompue. Les réponses partielles ont été supprimées.",
          threadId,
          userId: "",
          feedback: false,
          createdAt: new Date(),
        };
        return dedupeUserMessages(
          filterMessagesForDisplay([...head, interruptMsg])
        );
      });
      /** Après avoir figé l’UI : couper le SSE et vider le buffer (évite de réinjecter les chunks). */
      abortQuery();
    },
    [abortQuery]
  );

  // Update messages state with new SSE messages
  useEffect(() => {
    // log.debug("Updating messages state with new SSE messages"); // Log message update 
    setMessages((prevMessages) => {
      // Filtrer les messages SSE qui ne concernent pas le thread courant pour éviter
      // que des réponses d’un autre chat « débordent » dans la conversation active.
      const filteredIncoming = messagesQuery.filter((m) => {
        const tid = m.threadId;
        const currentTid = selectedThreadIdRef.current;
        // En "Nouveau chat" (aucun thread sélectionné), ne jamais afficher un stream
        // d'un thread précédent.
        if (!currentTid) return false;
        return tid === currentTid;
      });
      /** Après abort, le buffer SSE est vidé : ne pas « fusionner » un lot vide (écrase le message d’interruption). */
      if (filteredIncoming.length === 0) {
        return prevMessages;
      }
      // Si un stream est repris et renvoie à nouveau des chunks depuis le début,
      // garder le texte local le plus avancé pour éviter un retour visuel à 0.
      const prevById = new Map(prevMessages.map((m) => [String(m.id), m]));
      const reconciledIncoming = filteredIncoming.map((incoming) => {
        const prev = prevById.get(String(incoming.id));
        if (!prev) return incoming;
        if (
          typeof prev.content === "string" &&
          typeof incoming.content === "string" &&
          prev.content.length > incoming.content.length &&
          prev.content.startsWith(incoming.content)
        ) {
          return { ...incoming, content: prev.content, createdAt: prev.createdAt };
        }
        return incoming;
      });

      const sseMessageIds = new Set(reconciledIncoming.map((obj) => obj.id));
      // log.debug("SSE messages ID", sseMessageIds);
      // Filtrer les messages de reformulation des messages précédents
      let oldMessages = prevMessages
        .filter((m) => !sseMessageIds.has(m.id))
        .filter((m) => m.type !== "reformulation");

      // Éviter doublon user : le back renvoie la bulle user ; on retire l’optimiste même texte.
      for (const incoming of reconciledIncoming) {
        if (incoming.type === "user" && typeof incoming.content === "string") {
          oldMessages = oldMessages.filter(
            (m) =>
              !(
                m.type === "user" &&
                String(m.id).startsWith("optimistic-") &&
                m.content === incoming.content
              )
          );
        }
      }
      // log.debug("Old messages", oldMessages);
      const combined = [...oldMessages, ...reconciledIncoming];

      // UX: si le "thought" optimiste est injecté avant que le SSE ne renvoie le message user,
      // il peut apparaître au-dessus de la question. On le repousse juste après le user du même thread.
      const byThreadUserMaxT = new Map<string, number>();
      for (const m of combined) {
        if (m.type !== "user") continue;
        const t = m.threadId;
        const mt = m.createdAt.getTime();
        const prev = byThreadUserMaxT.get(t) ?? 0;
        if (mt > prev) byThreadUserMaxT.set(t, mt);
      }

      const reordered = combined.map((m) => {
        if (
          m.type !== "thought" ||
          !String(m.id).startsWith("optimistic-thought-")
        ) {
          return m;
        }
        const userMaxT = byThreadUserMaxT.get(m.threadId);
        if (!userMaxT) return m;
        const mt = m.createdAt.getTime();
        if (mt > userMaxT) return m;
        return { ...m, createdAt: new Date(userMaxT + 1) };
      });
      return dedupeUserMessages(filterMessagesForDisplay(reordered));
    });
  }, [messagesQuery]);

  // Persiste les messages (SSE) pour permettre une reprise sans refresh :
  // le backend ne renvoie parfois que le "nouveau" pendant un job en cours.
  useEffect(() => {
    const tid = selectedThread?.id;
    if (!tid) return;
    saveDraftMessages(tid, messages);
  }, [messages, selectedThread?.id]);

  const messageTypeOrder = useCallback((t: string) => {
    const order: Record<string, number> = {
      user: 0,
      reformulation: 1,
      thought: 2,
      source: 3,
      sources_extended: 4,
      answer: 5,
      rag_synthesis: 6,
      final_synthesis: 7,
      system: 8,
    };
    return order[t] ?? 15;
  }, []);

  // Transform messages into chat items and sort them by creation time
  useEffect(() => {
    const allChatItems = transformList(messages);
    const sortedChatItems = allChatItems.sort((a, b) => {
      const ta = a.createdAt.getTime();
      const tb = b.createdAt.getTime();
      if (ta !== tb) return ta - tb;
      const ra =
        "panels" in a && (a as { type?: string }).type === "carousel"
          ? 40
          : messageTypeOrder((a as Message).type);
      const rb =
        "panels" in b && (b as { type?: string }).type === "carousel"
          ? 40
          : messageTypeOrder((b as Message).type);
      return ra - rb;
    });
    setChatItems(sortedChatItems);
  }, [messages, transformList, messageTypeOrder]);

  // Function to send a custom payload to the backend
  const sendQuery = useCallback(
    async (payload: { query?: string; [key: string]: any }) => {
      log.info("Sending query to backend:", payload); // Log query send

      try {
        const threadId =
          (payload.threadId as string | undefined) ??
          selectedThread?.id ??
          uuidv4();
        const finalPayload = {
          ...payload,
          threadId,
        };
        if (!selectedThread) {
          // Nouveau chat : fixer tout de suite le thread courant pour que
          // l'affichage SSE ne récupère pas les messages d'un ancien thread.
          changeThread(
            {
              id: threadId,
              title: "Conversation",
              updatedAt: new Date(),
            } as Thread,
            false
          );
        }
        await executeQuery("POST", chatEndpoint, finalPayload);
      } catch (error) {
        const errorMessage = errorToString(error);
        log.error("Error sending query:", errorMessage);
        addError(`Failed to send query: ${errorMessage}`);
      }
    },
    [chatEndpoint, executeQuery, selectedThread, addError, changeThread]
  );

  /**
   * Rattache le SSE au job exhaustive encore en RAM.
   * 404 / ExhaustiveSearchResumeNoJobError = normal dès que le job est fini ou sur une autre instance :
   * il n’y a plus rien à « reprendre » en flux — l’historique doit venir de l’API fil (messages persistés).
   * Second fetch léger après 2s pour absorber un léger décalage ES après fin de stream.
   */
  const resynthesizeExhaustiveFinal = useCallback(
    async (threadId: string, page: string) => {
      try {
        await executeQuery(
          "POST",
          endpointsCf.exhaustiveSearchResynthesizeFinal(),
          { threadId, page }
        );
      } catch (error) {
        const errorMessage = errorToString(error);
        log.error("Exhaustive final resynthesis failed:", errorMessage);
        addError(`Échec de la resynthèse finale : ${errorMessage}`);
      }
    },
    [executeQuery, addError]
  );

  const resumeExhaustiveSearchStream = useCallback(
    async (threadId: string) => {
      if (!exhaustiveSearchResumeEndpoint) return;
      try {
        await executeQuery("POST", exhaustiveSearchResumeEndpoint, { threadId });
      } catch (error) {
        const errorMessage = errorToString(error);
        const noActiveJob =
          /404|No active exhaustive search job/i.test(errorMessage) ||
          (isError(error) &&
            error.name === "ExhaustiveSearchResumeNoJobError");
        if (noActiveJob) {
          log.debug(
            "Exhaustive search resume: no active job (expected after completion or scale-out), reloading thread:",
            errorMessage
          );
          await fetchMessages(threadId);
          window.setTimeout(() => {
            void fetchMessages(threadId);
          }, 2000);
          return;
        }
        log.error("Exhaustive search resume failed:", errorMessage);
        addError(`Failed to resume stream: ${errorMessage}`);
      }
    },
    [exhaustiveSearchResumeEndpoint, executeQuery, addError, fetchMessages]
  );

  // Reset buffers and update message history when thread changes
  useEffect(() => {
    // log.debug("Thread changed, resetting buffers and updating message history");    
    // Reset buffers when the thread changes
    buffers.current = {};
    // Update messages with the messages from the selected thread,
    // en fusionnant avec un cache local (messages streamés avant navigation).
    const tid = selectedThread?.id;
    if (!tid) {
      setMessages(dedupeUserMessages(filterMessagesForDisplay(threadMessages)));
      return;
    }
    const draft = loadDraftMessages(tid);
    const mergedById = new Map<string, Message>();
    for (const m of [...threadMessages, ...draft]) {
      const key = String(m.id);
      const prev = mergedById.get(key);
      mergedById.set(key, prev ? pickRicherMessage(prev, m) : m);
    }
    const merged = sortMessagesStable(Array.from(mergedById.values()));

    // Dès que le backend fournit la version "complète" (carousel/final), on peut purger le cache.
    const hasComplete =
      threadMessages.some((m) => Boolean(m.carousel)) ||
      threadMessages.some((m) => m.type === "final_synthesis");
    if (hasComplete) clearDraftMessages(tid);

    setMessages(dedupeUserMessages(filterMessagesForDisplay(merged)));
  }, [threadMessages]);

  const clearChat = useCallback(() => {
    setChatItems([]);
  }, []);

  // log.info("useChat hook ready to use"); // Logging hook readiness
  // log.debug("Current message buffers", buffers);
  // log.debug("Current messages", messages);
  // log.debug("Current chat items", chatItems);
  return {
    threads,
    changeThread,
    deleteThread,
    renameThread,
    chatItems,
    sendQuery,
    abortQuery,
    isLoadingQuery,
    isLoadingSelectedThread,
    activeStreamThreadIds: activeThreadIds,
    fetchMessages,
    selectedThread,
    selectedSource,
    changeSelectedSource,
    setFilters: changeThread,
    clearChat,
    resumeExhaustiveSearchStream,
    resynthesizeExhaustiveFinal,
    appendOptimisticUserMessage,
    removeOptimisticUserMessages,
    appendOptimisticThoughtMessage,
    upsertOptimisticThoughtMessage,
    removeOptimisticThoughtMessages,
    interruptStreamForThread,
  };
};
